"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.OPEventRecordUpdated = exports.OPEventRecordCommentUpdated = exports.OPEventRecordMetaUpdated = void 0;
const event_manager_1 = require("../../../event_manager");
const lodash_1 = require("lodash");
const store_1 = require("../../../exports/store");
const types_1 = require("../../../types");
const interface_1 = require("../interface");
const const_1 = require("./../../const");
class OPEventRecordMetaUpdated extends interface_1.IAtomEventType {
    constructor() {
        super(...arguments);
        this.eventName = const_1.OPEventNameEnums.RecordMetaUpdated;
        this.realType = event_manager_1.EventRealTypeEnums.REAL;
        this.scope = types_1.ResourceType.Datasheet;
    }
    test({ action, resourceId }) {
        const { pass, recordId } = (0, event_manager_1.testPath)(action.p, ['recordMap', ':recordId', 'recordMeta']);
        return {
            pass,
            context: {
                datasheetId: resourceId,
                recordId,
                action,
            }
        };
    }
}
exports.OPEventRecordMetaUpdated = OPEventRecordMetaUpdated;
class OPEventRecordCommentUpdated {
    constructor() {
        this.eventName = const_1.OPEventNameEnums.RecordCommentUpdated;
        this.realType = event_manager_1.EventRealTypeEnums.REAL;
        this.scope = types_1.ResourceType.Datasheet;
        this.atomType = event_manager_1.EventAtomTypeEnums.ATOM;
    }
    test(opContext, _sourceType) {
        const { action, resourceId } = opContext;
        const { pass, recordId } = (0, event_manager_1.testPath)(action.p, ['recordMap', ':recordId', 'comments', ':commentIndex']);
        return {
            pass: pass,
            context: {
                datasheetId: resourceId,
                recordId: recordId,
                action,
            }
        };
    }
}
exports.OPEventRecordCommentUpdated = OPEventRecordCommentUpdated;
class OPEventRecordUpdated extends interface_1.ICombEventType {
    constructor() {
        super(...arguments);
        this.eventName = const_1.OPEventNameEnums.RecordUpdated;
        this.realType = event_manager_1.EventRealTypeEnums.REAL;
        this.scope = types_1.ResourceType.Datasheet;
        this.acceptEventNames = [const_1.OPEventNameEnums.CellUpdated];
    }
    comb(events) {
        const res = [];
        const eventBuffer = events.filter(event => this.acceptEventNames.includes(event.eventName));
        const groupEvents = (0, lodash_1.groupBy)(eventBuffer, ({ context }) => {
            const { datasheetId, recordId } = context;
            return `${datasheetId}-${recordId}`;
        });
        Object.keys(groupEvents).forEach(dstRecordId => {
            const [datasheetId, recordId] = dstRecordId.split('-');
            // log all cell update events
            const events = groupEvents[dstRecordId];
            const recordChange = {};
            const diffFields = [];
            // console.log('comb events', events);
            events.forEach(event => {
                if (event.context.change) {
                    recordChange[event.context.fieldId] = event.context.change.to;
                }
                diffFields.push(event.context.fieldId);
            });
            res.push({
                eventName: const_1.OPEventNameEnums.RecordUpdated,
                context: {
                    datasheetId,
                    recordId,
                    fields: recordChange,
                    diffFields
                },
                scope: this.scope,
                realType: this.realType,
                atomType: this.atomType,
                sourceType: const_1.EventSourceTypeEnums.ALL,
            });
        });
        return res;
    }
    /**
     * When generating events through op, some fields need to be completed with the help of state context. Complete the event context here
     */
    fill(events, state) {
        return events.map(event => {
            var _a;
            if (event.eventName === const_1.OPEventNameEnums.RecordUpdated) {
                const { datasheetId, recordId } = event.context;
                event.context.datasheetName = (_a = store_1.Selectors.getDatasheet(state, datasheetId)) === null || _a === void 0 ? void 0 : _a.name;
                event.context.state = state;
                const { fields, eventFields } = (0, event_manager_1.transformOpFields)({
                    recordData: event.context.fields,
                    state: state,
                    datasheetId,
                    recordId
                });
                // This fields are used for trigger filter verification, where fieldValue is the cellValue from op
                event.context.fields = fields;
                // This eventFields is used for trigger output, where fieldValue is eventCV converted by cv
                event.context.eventFields = eventFields;
            }
            return event;
        });
    }
}
exports.OPEventRecordUpdated = OPEventRecordUpdated;
//# sourceMappingURL=record_updated.js.map